// Type1ContinueModal.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faTimes,
  faUserMd,
} from "@fortawesome/free-solid-svg-icons";
import CreatableSelect from "react-select/creatable";
import axios from "axios";
import { BASE_URL } from "../../config";
import { toast } from "react-toastify";
import { customStyles } from "./selectStyles";

const formatProfessionalFee = (value) => {
  if (value === null || value === undefined) return "N/A";

  const normalizedValue = String(value).trim();
  if (
    normalizedValue === "" ||
    normalizedValue === "-" ||
    normalizedValue.toUpperCase() === "N/A"
  ) {
    return "N/A";
  }

  const amount = Number(normalizedValue.replace(/,/g, ""));
  if (!Number.isFinite(amount)) return normalizedValue;

  return `PHP ${amount.toLocaleString("en-PH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const getPhysicianOptionLabel = (option = {}) =>
  option.label ||
  option.full_name_w_id ||
  option.full_name ||
  option.physician_name ||
  option.name ||
  "";

const getPhysicianOptionValue = (option = {}) =>
  option.value || option.physician_id || option.tbl_id || option.id || "";

const Type1ContinueModal = ({
  isOpen,
  onClose,
  selectedProvider,
  selectedCoverage,
  transactionData,
  physicianOptions,
  selectedPhysician,
  setSelectedPhysician,
  physicianEntryMode,
  setPhysicianEntryMode,
  freeTextPhysicianName,
  setFreeTextPhysicianName,
  chiefComplaint,
  setChiefComplaint,
  onGenerateLoa,
  loading,
}) => {
  const [localPhysicians, setLocalPhysicians] = useState([]);
  const [shouldBump, setShouldBump] = useState(false);
  const modalRef = useRef(null);

  const activePhysicianEntryMode = physicianEntryMode || "dropdown";
  const physicianOptionsSource = localPhysicians.length > 0 ? localPhysicians : physicianOptions;
  const selectedPhysicianValue = useMemo(() => {
    if (activePhysicianEntryMode === "free_text") {
      const name = String(freeTextPhysicianName || "").trim();
      return name
        ? {
            label: name,
            value: name,
            __isNew__: true,
            isFreeText: true,
          }
        : null;
    }

    return selectedPhysician || null;
  }, [activePhysicianEntryMode, freeTextPhysicianName, selectedPhysician]);

  const setExistingPhysician = (option) => {
    setPhysicianEntryMode?.("dropdown");
    setFreeTextPhysicianName?.("");
    setSelectedPhysician?.(option);
  };

  const setFreeTextPhysician = (inputValue) => {
    const physicianName = String(inputValue || "").trim();
    setPhysicianEntryMode?.("free_text");
    setFreeTextPhysicianName?.(physicianName);
    setSelectedPhysician?.({
      label: physicianName,
      value: physicianName,
      __isNew__: true,
      isFreeText: true,
    });
  };

  const handlePhysicianChange = (option) => {
    if (!option) {
      setPhysicianEntryMode?.("dropdown");
      setFreeTextPhysicianName?.("");
      setSelectedPhysician?.(null);
      return;
    }

    if (option.__isNew__ || option.isFreeText) {
      setFreeTextPhysician(getPhysicianOptionLabel(option) || getPhysicianOptionValue(option));
      return;
    }

    setExistingPhysician(option);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setShouldBump(true);
        setTimeout(() => setShouldBump(false), 300);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === "Escape" && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscapeKey);
    }

    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen && selectedProvider?.id) {
      axios
        .get(`${BASE_URL}/generate-loa/providers/${selectedProvider.id}/physicians`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("authentication/token")}` },
        })
        .then((res) => {
          if (res.data.status === "success") {
            setLocalPhysicians(res.data.data);
          }
        })
        .catch(() => toast.error("Failed to load physicians."));
    }
  }, [isOpen, selectedProvider]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-y-0 left-0 tracker-aware-modal-layer bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
      >
        <motion.div
          ref={modalRef}
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{
            scale: shouldBump ? [1, 1.02, 1] : 1,
            opacity: 1,
          }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{
            duration: shouldBump ? 0.3 : 0.2,
            ease: shouldBump ? "easeInOut" : "easeOut",
          }}
          className="bg-white rounded-2xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto shadow-2xl border border-gray-100"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl text-gray-900 flex items-center gap-2">
              <FontAwesomeIcon icon={faUserMd} className="text-indigo-600" />
              Letter of Authorization for {selectedCoverage?.text || "Consultation"}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <FontAwesomeIcon icon={faTimes} className="text-xl" />
            </button>
          </div>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Physician <span className="text-red-500">*</span>
              </label>
              <CreatableSelect
                placeholder="Search physician or type a new physician name"
                value={selectedPhysicianValue}
                onChange={handlePhysicianChange}
                onCreateOption={setFreeTextPhysician}
                options={physicianOptionsSource}
                isClearable
                isLoading={loading}
                styles={customStyles}
                getOptionLabel={getPhysicianOptionLabel}
                getOptionValue={getPhysicianOptionValue}
                formatCreateLabel={(inputValue) => `Use free-text physician: ${inputValue}`}
                isValidNewOption={(inputValue) => String(inputValue || "").trim().length > 0}
              />
              {activePhysicianEntryMode === "free_text" && (
                <p className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  This physician will be saved to the LOA as not yet enrolled.
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Chief Complaint <span className="text-red-500">*</span>
              </label>
              <textarea
                value={chiefComplaint}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value.length <= 1000) {
                    setChiefComplaint(value);
                  }
                }}
                placeholder="Enter Chief Complaint"
                rows={4}
                className={`w-full p-3 border rounded-md focus:ring-2 focus:border-transparent resize-none transition ${
                  chiefComplaint.length >= 950
                    ? "border-yellow-500 focus:ring-yellow-500"
                    : "border-gray-300 focus:ring-indigo-500"
                }`}
              />
              <div className="text-right text-sm mt-1">
                <span
                  className={`${
                    chiefComplaint.length >= 1000
                      ? "text-red-600 font-semibold"
                      : chiefComplaint.length >= 950
                      ? "text-yellow-600"
                      : "text-gray-500"
                  }`}
                >
                  {chiefComplaint.length}/1000 characters
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-500">
                  Benefit for Professional Fee
                </label>
                <p className="text-lg font-semibold text-gray-900">
                  {selectedCoverage?.text || "Consultation"}
                </p>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-500">PCP Rate</label>
                <p className="text-lg font-semibold text-gray-900">
                  {formatProfessionalFee(transactionData?.prof_fee)}
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Special Instructions
              </label>
              <textarea
                readOnly
                value={transactionData?.special_instructions || "N/A"}
                className="w-full p-3 border border-gray-300 rounded-md resize-vertical"
                rows={4}
                placeholder="Special Instructions"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="px-6 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors"
              onClick={onGenerateLoa}
            >
              <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
              Generate LOA
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="px-6 py-3 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors"
              onClick={onClose}
            >
              Close
            </motion.button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default Type1ContinueModal;
